import { useState, useEffect, useCallback, useRef } from 'react';
import type { UUID, InspectionStatusItem } from '../types/api';
import { getBatchStatus, ApiError } from '../api';

interface UseStatusPollingOptions {
  inspectionIds: UUID[];
  apiKey?: string;
  enabled?: boolean;
  interval?: number; // ms, default 5000
  onStatusChange?: (items: InspectionStatusItem[]) => void;
}

interface UseStatusPollingReturn {
  items: InspectionStatusItem[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  stop: () => void;
  start: () => void;
}

export function useStatusPolling({
  inspectionIds,
  apiKey,
  enabled = true,
  interval = 5000,
  onStatusChange,
}: UseStatusPollingOptions): UseStatusPollingReturn {
  const [items, setItems] = useState<InspectionStatusItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<number | null>(null);
  const isActiveRef = useRef(enabled);

  const fetchStatus = useCallback(async () => {
    if (inspectionIds.length === 0) {
      setItems([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const results = await getBatchStatus(inspectionIds, apiKey);
      setItems(results);
      onStatusChange?.(results);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Erro ao buscar status');
      }
    } finally {
      setLoading(false);
    }
  }, [inspectionIds, apiKey, onStatusChange]);

  const start = useCallback(() => {
    isActiveRef.current = true;
    fetchStatus();
    if (intervalRef.current === null) {
      intervalRef.current = window.setInterval(fetchStatus, interval);
    }
  }, [fetchStatus, interval]);

  const stop = useCallback(() => {
    isActiveRef.current = false;
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (enabled) {
      start();
    } else {
      stop();
    }

    return () => stop();
  }, [enabled, start, stop]);

  // Restart when IDs change
  useEffect(() => {
    if (isActiveRef.current) {
      fetchStatus();
    }
  }, [inspectionIds, fetchStatus]);

  return { items, loading, error, refresh, stop, start };
}

// Hook for single inspection status
export function useInspectionStatus(
  inspectionId: UUID | null,
  apiKey?: string,
  options: Omit<UseStatusPollingOptions, 'inspectionIds'> = {}
) {
  const ids = inspectionId ? [inspectionId] : [];
  const { items, ...rest } = useStatusPolling({ ...options, inspectionIds: ids, apiKey });
  
  return {
    ...rest,
    status: items[0]?.status,
    item: items[0],
    isProcessing: items[0]?.status === 'processing',
    isDone: items[0]?.status === 'done',
    isFailed: items[0]?.status === 'failed',
  };
}